import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { videoCategories, allVideos, type VideoItem } from '@/mocks/videos';

export default function Category() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredVideos, setFilteredVideos] = useState<VideoItem[]>(allVideos);

  useEffect(() => {
    setActiveCategory(slug || 'all');
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredVideos(allVideos);
    } else {
      setFilteredVideos(allVideos.filter((v) => v.category === activeCategory));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = videoCategories.find((c) => c.slug === activeCategory);

  const breadcrumbItems = [
    { label: '分类浏览', path: '/category/all' },
    { label: currentCategory?.name || '全部' },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `豆花视频 - ${currentCategory?.name || '全部'}内容`,
    description: `浏览豆花视频${currentCategory?.name || '全部'}分类下的精彩视频内容`,
    url: `https://www.douhuavideo.com/category/${activeCategory}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.douhuavideo.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: 'https://www.douhuavideo.com/category/all',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: currentCategory?.name || '全部',
      },
    ],
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              {currentCategory?.name || '全部'}内容
            </h1>

            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
              {videoCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 mb-12">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative overflow-hidden aspect-video">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <i className="ri-play-fill text-2xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {video.rating}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {video.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}